import React, { useEffect, useState } from "react";
import { X, AlertCircle } from "lucide-react";

interface ErrorMessageProps {
  message: string | null;
  onClose?: () => void;
  duration?: number;
  variant?: 'default' | 'dark' | 'light';
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
  message,
  onClose,
  duration = 5000,
  variant,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  // Keep the last non-null message so the slide-out animation still shows
  // text instead of flashing an empty shell while message has gone to null.
  const [lastMessage, setLastMessage] = useState<string | null>(null);

  useEffect(() => {
    if (message) {
      setLastMessage(message);
      setShouldRender(true);
      const showTimer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(showTimer);
    }
    // Message cleared externally — slide out then unmount.
    setIsVisible(false);
    const hideTimer = setTimeout(() => {
      setShouldRender(false);
      setLastMessage(null);
    }, 300);
    return () => clearTimeout(hideTimer);
  }, [message]);

  useEffect(() => {
    if (message && onClose && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [message, onClose, duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      setLastMessage(null);
      onClose?.();
    }, 300);
  };

  if (!shouldRender || !lastMessage) return null;

  return (
    <div
      className={`error-message error-message-${variant || 'default'} ${isVisible ? "visible" : "hidden"}`}
      role="alert"
    >
      <AlertCircle className="error-icon" />
      <span className="error-text">{lastMessage}</span>
      {onClose && (
        <button
          onClick={handleClose}
          className="error-close-button"
        >
          <X className="close-icon" />
          <span className="sr-only">Close</span>
        </button>
      )}
    </div>
  );
};

export default ErrorMessage;